"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * Behaviour for the server-rendered header: active-link highlighting, mega menu
 * hover/click/Escape handling and the mobile menu toggle. Works on the existing
 * markup so the header itself stays a static server component.
 */
export function NavBehavior() {
  const pathname = usePathname();

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    const timers: number[] = [];

    const menuBtn = document.getElementById("menu-toggle");
    const mobileMenu = document.getElementById("mobile-menu");

    const closeAll = () => {
      document.querySelectorAll(".nav-group[open]").forEach((d) => d.removeAttribute("open"));
      mobileMenu?.classList.remove("open");
      menuBtn?.setAttribute("aria-expanded", "false");
    };

    /* Active page */
    document.querySelectorAll("[data-active]").forEach((el) => el.removeAttribute("data-active"));
    document.querySelectorAll<HTMLAnchorElement>(".nav-links > a, .mobile-menu a, .nav-mega-item").forEach((a) => {
      const href = a.getAttribute("href");
      if (href && href === pathname) {
        a.setAttribute("data-active", "true");
        a.closest(".nav-group")?.querySelector("summary")?.setAttribute("data-active", "true");
      }
    });

    closeAll();

    /* Desktop hover: one mega menu open at a time */
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      document.querySelectorAll<HTMLElement>(".nav-group").forEach((g) => {
        let t = 0;
        g.addEventListener(
          "mouseenter",
          () => {
            window.clearTimeout(t);
            document.querySelectorAll(".nav-group[open]").forEach((other) => {
              if (other !== g) other.removeAttribute("open");
            });
            g.setAttribute("open", "");
          },
          { signal }
        );
        g.addEventListener(
          "mouseleave",
          () => {
            t = window.setTimeout(() => g.removeAttribute("open"), 150);
            timers.push(t);
          },
          { signal }
        );
      });
    }

    /* Outside click / Escape */
    document.addEventListener(
      "click",
      (e) => {
        document.querySelectorAll(".nav-group[open]").forEach((d) => {
          if (!d.contains(e.target as Node)) d.removeAttribute("open");
        });
      },
      { signal }
    );
    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape") closeAll();
      },
      { signal }
    );

    /* Mobile menu */
    menuBtn?.addEventListener(
      "click",
      () => {
        const open = mobileMenu?.classList.toggle("open") ?? false;
        menuBtn.setAttribute("aria-expanded", String(open));
      },
      { signal }
    );

    return () => {
      ac.abort();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [pathname]);

  return null;
}
